import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_STYLES = {
  cyan: 'from-cyan-600 to-blue-600',
  gold: 'from-amber-500 to-yellow-600',
  green: 'from-emerald-600 to-green-600',
  red: 'from-rose-600 to-red-600',
  dark: 'from-slate-900 to-slate-700',
};

export const useAnnouncement = () => {
  const [ann, setAnn] = useState(null);
  useEffect(() => {
    const load = () => {
      fetch(`${API_URL}/api/announcement?_t=${Date.now()}`, { cache: 'no-store' })
        .then(r => (r.ok ? r.json() : null))
        .then(d => setAnn(d && d.enabled ? d : null))
        .catch(() => {});
    };
    load();
    window.addEventListener('productsUpdated', load);
    return () => window.removeEventListener('productsUpdated', load);
  }, []);
  return ann;
};

const useAnnText = (ann) => {
  const { language } = useLanguage();
  if (!ann) return {};
  const pick = (base) => {
    if (language === 'en') return ann[`${base}_en`] || ann[base];
    if (language === 'he') return ann[`${base}_he`] || ann[base];
    return ann[base];
  };
  return {
    title: pick('title'),
    subtitle: pick('subtitle'),
    cta: pick('cta_text'),
  };
};

const copyCoupon = (code, language) => {
  navigator.clipboard?.writeText(code);
  const msg = language === 'es' ? `Cupón ${code} copiado` : language === 'he' ? `קופון ${code} הועתק` : `Coupon ${code} copied`;
  toast.success(msg);
};

export const SeasonalBar = ({ ann }) => {
  const { language } = useLanguage();
  const { title, cta } = useAnnText(ann);
  const [closed, setClosed] = useState(() => sessionStorage.getItem('annBarClosed') === '1');
  if (!ann || closed || (ann.type !== 'bar' && ann.type !== 'both')) return null;
  return (
    <div className={`relative z-[55] bg-gradient-to-r ${COLOR_STYLES[ann.color] || COLOR_STYLES.cyan} text-white`} data-testid="seasonal-bar">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2 sm:gap-3 text-center">
        <span className="text-base leading-none">{ann.emoji}</span>
        <p className="text-xs sm:text-sm font-bold tracking-wide truncate">{title}</p>
        {ann.coupon_code && (
          <button
            onClick={() => copyCoupon(ann.coupon_code, language)}
            className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-black tracking-wider transition-colors"
            data-testid="announcement-bar-coupon"
          >
            {ann.coupon_code} <Copy className="w-3 h-3" />
          </button>
        )}
        {cta && !ann.coupon_code && <span className="hidden sm:inline text-xs font-semibold underline">{cta}</span>}
        <button
          onClick={() => { sessionStorage.setItem('annBarClosed', '1'); setClosed(true); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
          data-testid="announcement-bar-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const SeasonalPopup = ({ ann }) => {
  const { language } = useLanguage();
  const { title, subtitle, cta } = useAnnText(ann);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (ann && (ann.type === 'popup' || ann.type === 'both') && sessionStorage.getItem('annPopupSeen') !== '1') {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [ann]);
  const close = useCallback(() => { sessionStorage.setItem('annPopupSeen', '1'); setOpen(false); }, []);
  if (!ann || !open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="seasonal-popup">
      <div
        className={`relative w-full max-w-sm rounded-2xl overflow-hidden bg-gradient-to-br ${COLOR_STYLES[ann.color] || COLOR_STYLES.cyan} text-white shadow-2xl p-7 text-center animate-in fade-in zoom-in-95 duration-300`}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={close} className="absolute top-3 right-3 text-white/70 hover:text-white" data-testid="announcement-popup-close">
          <X className="w-5 h-5" />
        </button>
        <div className="text-5xl mb-3">{ann.emoji}</div>
        <h3 className="text-xl font-black tracking-tight leading-tight">{title}</h3>
        {subtitle && <p className="mt-2 text-sm text-white/85">{subtitle}</p>}
        {ann.coupon_code && (
          <button
            onClick={() => { copyCoupon(ann.coupon_code, language); close(); }}
            className="mt-4 inline-flex items-center gap-2 bg-white text-slate-900 font-black px-5 py-2.5 rounded-full text-sm tracking-wider hover:scale-105 transition-transform"
            data-testid="announcement-popup-coupon"
          >
            {ann.coupon_code} <Copy className="w-4 h-4" />
          </button>
        )}
        {cta && (
          <p className="mt-3 text-xs font-semibold text-white/80">{cta}</p>
        )}
      </div>
    </div>
  );
};
